import React, { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import { YhBox } from "@/components/YhBox";
import { YhFormBox } from "@/components/YhFormBox";
import { IYhFormBox, IYhFormItemsBox, FormItemTypeEnum, IYhFormRef, ISelectOption } from "@/components/YhFormBox/Types";
import { useMaterialModel } from "../materialModel";
import useNavModel, { RouterParamType } from "@/model/navModel";
import { getFormValidatorErrText } from "@/utils/utils"
import { useDictModel, ITreeSelectData } from "../../dict/dictModel";
import { DictCodeEnmu } from "@/types/AppDictEnums";
import useGlobalModel from "@/model/globalModel";
import { warehouseService, IpositionDetail } from "../../warehouse/warehouseService";

export const BaseMaterialDetails = () => {
    const {
        addFormVal,
        editFormVal,
        updateAddFormVal,
        updateEditFormVal,
        insertMaterial,
        editMaterial,
        getMaterialOne
    } = useMaterialModel();

    const { selectedNavId, replaceNav, getSelectedNavState, findNav } = useNavModel(({ selectedNavId }) => [selectedNavId]);
    const formRef = useRef<IYhFormRef>();
    const [detailsInfo] = useState<RouterParamType>(getSelectedNavState() as RouterParamType);
    const { getSelectedDictOne, getTreeSelectOne } = useDictModel(() => []);
    const { user: { shopId }, whDs,isEditable } = useGlobalModel(({ whDs, user }) => [whDs, user]);
    const formVal = detailsInfo.mode === "edit" ? editFormVal : addFormVal;

    //编辑时， 没有缓存调用详情接口
    useEffect(() => {
        if (!editFormVal.id && detailsInfo.mode === "edit") {
            getMaterialOne(detailsInfo.id).then(res => {
                res.warehouseCode && queryShelf(res.warehouseCode);
            })
        }
        formVal.warehouseCode && queryShelf(formVal.warehouseCode)
    }, [detailsInfo])

    //清楚缓存
    useEffect(() => {
        return () => {
            !findNav("7-1-1") && updateAddFormVal(null);
            !findNav("7-1-2") && updateEditFormVal(null);
        }
    }, [selectedNavId])

    //关闭当前页
    const onClose = () => {
        replaceNav(selectedNavId, "7-1");
    }

    //重置
    const onReset = () => {
        formRef.current?.resetFields();
        if (detailsInfo.mode === "edit") {
            getMaterialOne(detailsInfo.id)
        } else {
            updateAddFormVal(null);
        }
    };
    //保留缓存及回显表单
    const saveCache = (val: { [key: string]: any }, isFormItem: boolean = true) => {
        isFormItem && formRef.current?.setFieldsValue(val);
        detailsInfo.mode === "edit" ? updateEditFormVal(val) : updateAddFormVal(val);
    }

    //表单提交
    const onSubmit = async () => {
        formRef.current?.validateFields().then(val => {
            let data = {
                ...val,
                code: val.code?.trim(),
                name: val.name?.trim()
            }
            if (detailsInfo.mode === "edit") {
                editMaterial({ ...data, id: detailsInfo.id }).then(res => {
                    res && onClose();
                    updateEditFormVal(null);
                })
            } else {
                insertMaterial(data).then(res => {
                    res && onClose();
                    updateAddFormVal(null);
                })
            }
        }).catch(err => {
            message.warning(getFormValidatorErrText(err));
        })
    }

    //单位名称
    const unitNameChange = (value: string, option: ISelectOption) => {
        // debugger;
        option && saveCache({ unitName: option.label, unitCode: option.value });
    }

    //商品类型选择
    const typeCodeChange = (value: string, node: ITreeSelectData) => {
        node && saveCache({ typeName: node.title, typeCode: node.value });
    }

    //商品类型选择
    const brandNameChange = (value: string, option: ISelectOption) => {
        option && saveCache({ brandName: option.label, brandCode: option.value });
    }

    const [positionDs, setPositionDs] = useState<ISelectOption[]>([]);  //货位数据集

    //货位根据仓库code查询
    const queryShelf = async (warehouseCode: string) => {
        const searchParams = {
            shopId,
            warehouseCode,
            pageSize: -1,
            pageNum: 1,
        }
        let { retData } = await warehouseService.positionPage(searchParams);
        const { records, ...pg } = retData;
        setPositionDs(records.map(item => ({
            label: item.code ?? "",
            value: item.id
        })))
    }

    //仓库选择
    const warehouseChange = (value: string, option: ISelectOption) => {
        if (option) {
            saveCache({ warehouseName: option.label, warehouseCode: option.value, shelfCode: undefined, shelfId: undefined });
        }

    }

    //货位触角
    const positionFocus = () => {
        if (formVal["warehouseCode"]) {
            queryShelf(formVal["warehouseCode"] as string)
        } else {
            setPositionDs([]);
        }
    }

    //货位选择
    const positionChange = (value: string, option: ISelectOption) => {
        option && saveCache({ shelfCode: option.label, shelfId: option.value });
    }

    const onChange = (changedValues: { [key: string]: any }) => {
        saveCache(changedValues, false)
    }

    const baseFormItem: IYhFormItemsBox = {
        title: "基础信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "商品编码",
                fieldName: "code",
                disable: detailsInfo.mode === "edit",
                rules: [{ required: true, message: "请输入商品名称" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "商品名称",
                fieldName: "name",
                rules: [{ required: true, message: "请输入商品名称" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "商品名称-英文",
                fieldName: "nameEn",
                rules: [{ required: true, message: "请输入商品名称" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "规格型号",
                fieldName: "model",
            }, {
                type: FormItemTypeEnum.TreeSelect,
                fieldLabel: "商品类型",
                fieldName: "typeCode",
                onTreeSelectChange: typeCodeChange,
                treeData: getTreeSelectOne(DictCodeEnmu.MATERIAL_TYPE),
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "单位",
                fieldName: "unitCode",
                onSelectChange: unitNameChange,
                options: getSelectedDictOne(DictCodeEnmu.MATERIAL_UNIT),
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "产地",
                fieldName: "madeFrom",
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "品牌",
                fieldName: "brandCode",
                options: getSelectedDictOne(DictCodeEnmu.MATERIAL_BRAND),
                onSelectChange: brandNameChange,
                // rules: [{ required: true, message: "请选择品牌" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "备注",
                fieldName: "note",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "从属门店",
                fieldName: "shopName",
                disable: true
                // }, {
                //     type: FormItemTypeEnum.Select,
                //     fieldLabel: "所属仓库",
                //     fieldName: "warehouseCode",
                //     onSelectChange: warehouseChange,
                //     // rules: [{ required: true, message: "请选择所属仓库" }],
                //     options: whDs
                // }, {
                //     type: FormItemTypeEnum.Select,
                //     fieldLabel: "所属货位",
                //     fieldName: "shelfId",
                //     onSelectFocus: positionFocus,
                //     onSelectChange: positionChange,
                //     options: positionDs
            },

            //带出
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "商品类型名称",
                fieldName: "typeName",
                hidden: true
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "单位名称",
                fieldName: "unitName",
                hidden: true
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "品牌名称",
                fieldName: "brandName",
                hidden: true
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "仓库名称",
                fieldName: "warehouseName",
                hidden: true
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "货位Code",
                fieldName: "shelfCode",
                hidden: true
            },
        ]
    };

    const tradeInfoFormItem: IYhFormItemsBox = {
        title: "贸易信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "海关编码",
                fieldName: "customsCode",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "增值税",
                fieldName: "vat",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "退税率",
                fieldName: "taxDrawback",
            }, {
                type: FormItemTypeEnum.TextArea,
                fieldLabel: "报关要素",
                fieldName: "customsEntry",
            }
        ]
    }
    const priceFormItem: IYhFormItemsBox = {
        title: "商品价格",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "进货价格",
                fieldName: "buyingPrice",
                // rules: [{ required: true, message: "请输入进货价" }, { pattern: /^(([1-9]{1}\d*)|(0{1}))(\.\d{1,2})?$/, message: '请输入数值类型,可保留二位位小数' }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "零售价格",
                fieldName: "sellingPrice",
                // rules: [{ required: true, message: "请输入进货价" }, { pattern: /^(([1-9]{1}\d*)|(0{1}))(\.\d{1,2})?$/, message: '请输入数值类型,可保留二位位小数' }]
            }
        ]
    }
    const otherFormItem: IYhFormItemsBox = {
        title: "其它信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "创建人",
                fieldName: "createUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "创建日期",
                fieldName: "createTime",
                disable: isEditable
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "修改人",
                fieldName: "updateUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "修改日期",
                fieldName: "updateTime",
                disable: isEditable
            },
            {
                type: FormItemTypeEnum.Switch,
                fieldLabel: "商品状态",
                fieldName: "status",
            }
        ]
    }

    const materialFromConfig: IYhFormBox<any> = {
        formRef,
        labelCol: { span: 8 },
        labelAlign: "right",
        formValues: formVal,
        onChange,
        // boxs: [baseFormItem, tradeInfoFormItem, priceFormItem, otherFormItem]
        boxs: [baseFormItem, tradeInfoFormItem, otherFormItem]
    }


    const action = <>
        {/* <YhAuth resCode={detailsInfo.mode === "edit" ? ResourceAuthEnum.DATA_S_ED_SAVE : ResourceAuthEnum.DATA_S_AD_SAVE}> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onSubmit}
        >保存</Button>
        {/* </YhAuth> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onReset}
        >重置</Button>
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onClose}
        >取消</Button>
    </>;

    return (
        <>
            <YhBox title={`商品${detailsInfo.mode === "edit" ? "编辑" : "新增"}`} action={action} style={{ paddingBottom: "0px", marginBottom: "10px" }}></YhBox>
            <YhFormBox {...materialFromConfig} />
        </>
    )
}